<script setup lang="ts">
import { computed, ref } from 'vue';

import { eqsApi } from '@/api/eqs';
import NodeRescheduleDialog from '@/components/NodeRescheduleDialog.vue';
import { useRescheduleAsk } from '@/composables/useRescheduleAsk';
import { notify } from '@/services/notice';
import { errorMessage } from '@/utils/format';
import { dayOf, rescheduleFields } from '@/utils/plan-node-reschedule';

const props = defineProps<{ projectId: string | number; canWrite?: boolean }>();
const emit = defineEmits<{ applied: [] }>();

interface Proposal {
  nodeId: number;
  name: string;
  phaseName: string | null;
  currentStart: string | null;
  currentEnd: string | null;
  nextStart: string | null;
  nextEnd: string | null;
  shiftDays: number | null;
  durationDays: number | null;
  previousDurationDays: number | null;
  viaCascade: boolean;
  evidence: string;
}

const open = ref(false);
const text = ref('');
const parsing = ref(false);
const applying = ref(false);
const proposals = ref<Proposal[]>([]);
const unmatched = ref<string[]>([]);
const picked = ref<Set<number>>(new Set());
const scanned = ref(0);
/**
 * 是否连带调整子任务。
 *
 * 之前这个范围由 AI 判断，判错就会一次误改十几个节点。改成显式开关：
 * 模型的判断只作为初始状态，最终由人决定。子任务后端一直算好了，
 * 切换开关不需要再调一次 AI。
 */
const cascade = ref(false);
const hasChildren = computed(() => proposals.value.some((item) => item.viaCascade));
/** 已排期的节点改了完成日要写原因（0926）：一批问一次，来源 planner */
const reschedule = useRescheduleAsk();

const SAMPLES = [
  '把 T0 首次试模推迟两周',
  'P03 阶段整体后移 10 天',
  'P03 阶段工期调整到 28 天',
  '模具评审延长 5 天',
  'GATE3 改到 2026-08-15',
];

async function parse(): Promise<void> {
  if (!text.value.trim()) {
    notify('error', '请先描述调整', '例如「P03 阶段整体后移 10 天」。');
    return;
  }
  parsing.value = true;
  proposals.value = [];
  unmatched.value = [];
  try {
    const result = await eqsApi.parseSchedule(props.projectId, text.value);
    proposals.value = (result.proposals ?? []) as unknown as Proposal[];
    unmatched.value = (result.unmatched ?? []) as string[];
    scanned.value = Number(result.scanned ?? 0);
    cascade.value = result.cascadeSuggested === true;
    syncPicked();
    if (!proposals.value.length) {
      notify('error', '没有可调整的节点', String(result.note ?? '换个说法，或带上节点名称试试。'));
    } else {
      notify('success', `将调整 ${proposals.value.length} 个节点`, '请核对新日期后再写入。');
    }
  } catch (caught) {
    notify('error', '解析失败', errorMessage(caught));
  } finally {
    parsing.value = false;
  }
}

/** 开关决定子任务选不选；父节点始终选中 */
function syncPicked(): void {
  picked.value = new Set(
    proposals.value
      .filter((item) => !item.viaCascade || cascade.value)
      .map((item) => item.nodeId),
  );
}

function setCascade(next: boolean): void {
  cascade.value = next;
  syncPicked();
}

function toggle(nodeId: number): void {
  const next = new Set(picked.value);
  if (next.has(nodeId)) next.delete(nodeId);
  else next.add(nodeId);
  picked.value = next;
}

const chosen = computed(() => proposals.value.filter((item) => picked.value.has(item.nodeId)));

/** 平移天数一致时给个总览，例会上一句「整体后移十天」看着就踏实 */
const summary = computed(() => {
  const days = new Set(chosen.value.map((item) => item.shiftDays).filter((d) => d !== null));
  if (days.size === 1) {
    const value = [...days][0] as number;
    return `${chosen.value.length} 个节点，整体${value > 0 ? '后移' : '提前'} ${Math.abs(value)} 天`;
  }
  // 改工期时说清楚是几天变几天，比只报节点数有用
  if (chosen.value.length === 1) {
    const only = chosen.value[0];
    if (only.durationDays !== null && only.previousDurationDays !== null
      && only.durationDays !== only.previousDurationDays) {
      return `1 个节点，工期 ${only.previousDurationDays} 天 → ${only.durationDays} 天`;
    }
  }
  return `${chosen.value.length} 个节点`;
});

async function apply(): Promise<void> {
  if (!chosen.value.length) return;
  // 原来有完成日、这次换了一天的那些要原因；没排过期的是补排期，不用
  const needing = new Set(chosen.value
    .filter((item) => item.currentEnd && item.nextEnd && dayOf(item.currentEnd) !== dayOf(item.nextEnd))
    .map((item) => item.nodeId));
  let extra: Record<string, string> = {};
  if (needing.size) {
    const answer = await reschedule.ask({ title: '按描述调整排期', fromDay: null, toDay: null, count: needing.size });
    if (!answer) return;
    extra = rescheduleFields(answer, 'planner');
  }
  applying.value = true;
  try {
    await eqsApi.batchUpdatePlanNodes(
      props.projectId,
      chosen.value.map((item) => ({
        nodeId: item.nodeId,
        ...(item.nextStart ? { plannedStartAt: isoDate(item.nextStart) } : {}),
        ...(item.nextEnd ? { plannedEndAt: isoDate(item.nextEnd, true) } : {}),
        ...(needing.has(item.nodeId) ? extra : {}),
      })),
    );
    notify('success', `已调整 ${chosen.value.length} 个节点`, '变更已记入进度事件与审计。');
    proposals.value = [];
    text.value = '';
    picked.value = new Set();
    emit('applied');
  } catch (caught) {
    notify('error', '写入失败', errorMessage(caught));
  } finally {
    applying.value = false;
  }
}

function isoDate(day: string, endOfDay = false): string {
  return new Date(`${day}T${endOfDay ? '23:59:59' : '00:00:00'}+08:00`).toISOString();
}
</script>

<template>
  <section v-if="canWrite !== false" class="schedule-planner">
    <header>
      <div>
        <span>SCHEDULE ASSISTANT</span>
        <h3>用一句话改排期</h3>
      </div>
      <button class="button" type="button" @click="open = !open">{{ open ? '收起' : '展开' }}</button>
    </header>

    <NodeRescheduleDialog
      v-if="reschedule.pending.value"
      :title="reschedule.pending.value.title"
      :from-day="reschedule.pending.value.fromDay"
      :to-day="reschedule.pending.value.toDay"
      :count="reschedule.pending.value.count"
      @confirm="reschedule.settle($event)"
      @cancel="reschedule.settle(null)"
    />

    <template v-if="open">
      <p class="sp-intro">
        直接说要怎么调，比如「P03 阶段整体后移 10 天」。
        <b>日期由系统计算，不由 AI 猜</b>；确认后才写入。
      </p>

      <textarea v-model="text" rows="2" :placeholder="SAMPLES[0]" aria-label="排期调整描述"></textarea>

      <div class="sp-actions">
        <button class="button button-primary" type="button" :disabled="parsing" @click="parse">
          {{ parsing ? '解析中…' : '解析调整' }}
        </button>
        <button
          v-for="sample in SAMPLES"
          v-show="!text"
          :key="sample"
          class="sp-sample"
          type="button"
          @click="text = sample"
        >{{ sample }}</button>
        <small v-if="scanned">已在 {{ scanned }} 个节点中匹配</small>
      </div>

      <ul v-if="unmatched.length" class="sp-unmatched">
        <li v-for="(line, index) in unmatched" :key="index">没能对上节点：{{ line }}</li>
      </ul>

      <div v-if="proposals.length" class="sp-proposals">
        <div class="sp-head">
          <b>{{ summary }}</b>
          <span>核对新日期，取消勾选可排除某一条。</span>
          <label v-if="hasChildren" class="sp-cascade-switch">
            <input
              type="checkbox"
              :checked="cascade"
              @change="setCascade(($event.target as HTMLInputElement).checked)"
            >
            <span>同时调整子任务（{{ proposals.filter((item) => item.viaCascade).length }} 条）</span>
          </label>
        </div>

        <table>
          <thead>
            <tr><th>选</th><th>节点</th><th>原计划</th><th>新计划</th></tr>
          </thead>
          <tbody>
            <tr v-for="item in proposals" :key="item.nodeId" :class="{ picked: picked.has(item.nodeId) }">
              <td>
                <input
                  type="checkbox"
                  :checked="picked.has(item.nodeId)"
                  :aria-label="`选择 ${item.name}`"
                  @change="toggle(item.nodeId)"
                >
              </td>
              <td>
                <b>{{ item.name }}</b>
                <small v-if="item.phaseName">{{ item.phaseName }}</small>
                <em v-if="item.viaCascade" class="sp-cascade">随阶段带出</em>
              </td>
              <td class="sp-date">{{ item.currentStart || '—' }} → {{ item.currentEnd || '—' }}</td>
              <td class="sp-date sp-next">
                {{ item.nextStart || '—' }} → {{ item.nextEnd || '—' }}
                <small
                  v-if="item.durationDays !== null && item.previousDurationDays !== null
                    && item.durationDays !== item.previousDurationDays"
                  class="sp-duration"
                >工期 {{ item.previousDurationDays }} → {{ item.durationDays }} 天</small>
              </td>
            </tr>
          </tbody>
        </table>

        <footer>
          <button
            class="button button-primary"
            type="button"
            :disabled="applying || !chosen.length"
            @click="apply"
          >{{ applying ? '写入中…' : `确认调整 ${chosen.length} 个节点` }}</button>
          <button class="button" type="button" :disabled="applying" @click="proposals = []">放弃</button>
        </footer>
      </div>
    </template>
  </section>
</template>

<style scoped>
.schedule-planner { display: grid; gap: 10px; padding: 14px 16px; margin: 14px 0; border: 1px solid var(--line); border-left: 3px solid var(--blue); background: var(--white); }
.schedule-planner > header { display: flex; align-items: flex-end; justify-content: space-between; gap: 10px; flex-wrap: wrap; }
.schedule-planner header span { color: var(--muted); font: 700 12px var(--display); letter-spacing: .08em; }
.schedule-planner header h3 { margin: 2px 0 0; font-size: 16px; }
.sp-intro { margin: 0; color: var(--muted); font-size: 13px; line-height: 1.8; }
.schedule-planner textarea { width: 100%; padding: 9px 11px; border: 1px solid var(--line); background: var(--white); font: inherit; font-size: 13px; line-height: 1.8; resize: vertical; }
.sp-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.sp-actions small { color: var(--muted); font-size: 12px; }
.sp-sample { padding: 3px 9px; border: 1px dashed var(--line); background: var(--paper); color: var(--muted); font-size: 12px; cursor: pointer; }
.sp-sample:hover { border-color: var(--blue); color: var(--blue); }
.sp-unmatched { margin: 0; padding: 8px 10px 8px 26px; border-left: 3px solid var(--warning-graphic); background: #fdf6ec; color: #7a4a10; font-size: 12px; line-height: 1.8; }
.sp-proposals { display: grid; gap: 8px; }
.sp-head { display: grid; gap: 2px; }
.sp-head b { font-size: 13px; }
.sp-head span { color: var(--muted); font-size: 12px; }
.sp-cascade-switch {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
  padding: 4px 9px;
  border: 1px solid var(--blue);
  background: #EEF4FA;
  color: var(--blue);
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  width: fit-content;
}

.sp-proposals table { width: 100%; border-collapse: collapse; font-size: 12px; }
.sp-proposals th { padding: 6px 8px; border-bottom: 1px solid var(--line); color: var(--muted); font-weight: 600; text-align: left; }
.sp-proposals td { padding: 6px 8px; border-bottom: 1px solid var(--line); vertical-align: top; }
.sp-proposals tr { opacity: .55; }
.sp-proposals tr.picked { opacity: 1; }
.sp-proposals td b { display: block; font-size: 12px; }
.sp-proposals td small { display: block; color: var(--muted); font-size: 12px; }
.sp-cascade { display: inline-block; margin-top: 2px; padding: 0 5px; background: #EEF4FA; color: var(--blue); font-size: 12px; font-style: normal; }
.sp-date { white-space: nowrap; color: var(--muted); }
.sp-next { color: var(--ink); font-weight: 700; }
.sp-duration { display: block; margin-top: 2px; color: var(--blue); font-size: 12px; font-weight: 700; }
.sp-proposals footer { display: flex; gap: 8px; }
</style>
